import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { useLeagueConfig, DEFAULT_EMAIL_CONFIG, type EmailConfig } from '@/hooks/useLeagueConfig';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { LeagueSettingsLayout } from '@/components/LeagueSettingsLayout';
import { useToast } from '@/hooks/use-toast';
import { Save, Loader2, Mail } from 'lucide-react';

export default function LeagueEmails() {
  const { currentMembership, isSuperAdmin } = useAuth();
  const { toast } = useToast();
  const { emailConfig, isLoading: configLoading } = useLeagueConfig();

  const canEdit =
    isSuperAdmin ||
    currentMembership?.role === 'owner' ||
    currentMembership?.role === 'admin';

  const [emailEnabled, setEmailEnabled] = useState(DEFAULT_EMAIL_CONFIG.enabled_emails);
  const [reminderCadence, setReminderCadence] = useState<string[]>(DEFAULT_EMAIL_CONFIG.reminder_cadence);
  const [savingEmail, setSavingEmail] = useState(false);

  useEffect(() => {
    if (emailConfig) {
      setEmailEnabled(emailConfig.enabled_emails);
      setReminderCadence(emailConfig.reminder_cadence);
    }
  }, [emailConfig]);

  const handleSaveEmailConfig = async () => {
    if (!currentMembership?.league_id || !canEdit) return;

    setSavingEmail(true);
    const newEmailConfig: EmailConfig = {
      enabled_emails: emailEnabled,
      reminder_cadence: reminderCadence,
    };

    /* eslint-disable @typescript-eslint/no-explicit-any */
    const { error } = await (supabase
      .from('league_configs' as any)
      .update({ email_config: newEmailConfig })
      .eq('league_id', currentMembership.league_id) as any);
    /* eslint-enable @typescript-eslint/no-explicit-any */

    if (error) {
      toast({ title: 'Failed to save email settings', description: error.message, variant: 'destructive' });
    } else {
      toast({ title: 'Email settings saved' });
    }
    setSavingEmail(false);
  };

  const toggleCadence = (cadence: string) => {
    setReminderCadence(prev =>
      prev.includes(cadence) ? prev.filter(c => c !== cadence) : [...prev, cadence]
    );
  };

  if (!currentMembership) {
    return (
      <LeagueSettingsLayout activeTab="emails">
        <p className="text-stone-600">No league selected</p>
      </LeagueSettingsLayout>
    );
  }

  return (
    <LeagueSettingsLayout activeTab="emails">
        {configLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-stone-400" />
          </div>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Email Notifications
              </CardTitle>
              <CardDescription>
                {canEdit ? 'Configure which emails are sent to league members' : 'View email notification settings'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Email type toggles */}
              <div className="space-y-4">
                <Label className="text-sm font-medium">Email Types</Label>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                    <div>
                      <p className="font-medium text-sm">Matchday Open</p>
                      <p className="text-xs text-stone-500">Notify when picks open for a new matchday</p>
                    </div>
                    <Switch
                      checked={emailEnabled.matchday_open}
                      onCheckedChange={(checked) =>
                        setEmailEnabled(prev => ({ ...prev, matchday_open: checked }))
                      }
                      disabled={!canEdit}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                    <div>
                      <p className="font-medium text-sm">Deadline Reminders</p>
                      <p className="text-xs text-stone-500">Remind members before the pick deadline</p>
                    </div>
                    <Switch
                      checked={emailEnabled.matchday_reminder}
                      onCheckedChange={(checked) =>
                        setEmailEnabled(prev => ({ ...prev, matchday_reminder: checked }))
                      }
                      disabled={!canEdit}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                    <div>
                      <p className="font-medium text-sm">Matchday Selections</p>
                      <p className="text-xs text-stone-500">Share everyone's picks after the deadline</p>
                    </div>
                    <Switch
                      checked={emailEnabled.matchday_selections}
                      onCheckedChange={(checked) =>
                        setEmailEnabled(prev => ({ ...prev, matchday_selections: checked }))
                      }
                      disabled={!canEdit}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                    <div>
                      <p className="font-medium text-sm">Matchday Results</p>
                      <p className="text-xs text-stone-500">Send results and updated standings after matches</p>
                    </div>
                    <Switch
                      checked={emailEnabled.matchday_results}
                      onCheckedChange={(checked) =>
                        setEmailEnabled(prev => ({ ...prev, matchday_results: checked }))
                      }
                      disabled={!canEdit}
                    />
                  </div>
                </div>
              </div>

              {/* Reminder cadence */}
              {emailEnabled.matchday_reminder && (
                <div className="space-y-3">
                  <Label className="text-sm font-medium">Reminder Cadence</Label>
                  <p className="text-xs text-stone-500">
                    Choose when to send reminders before the deadline
                  </p>
                  <div className="flex flex-wrap gap-3">
                    {[
                      { key: '3d', label: '3 days' },
                      { key: '48h', label: '48 hours' },
                      { key: '24h', label: '24 hours' },
                      { key: '12h', label: '12 hours' },
                      { key: '6h', label: '6 hours' },
                      { key: '4h', label: '4 hours' },
                      { key: '1h', label: '1 hour' },
                    ].map(({ key, label }) => (
                      <label
                        key={key}
                        className="flex items-center gap-2 p-2 bg-stone-50 rounded border cursor-pointer hover:bg-stone-100"
                      >
                        <Checkbox
                          checked={reminderCadence.includes(key)}
                          onCheckedChange={() => toggleCadence(key)}
                          disabled={!canEdit}
                        />
                        <span className="text-sm">{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {canEdit && (
                <Button onClick={handleSaveEmailConfig} disabled={savingEmail}>
                  {savingEmail ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Save Email Settings
                </Button>
              )}
            </CardContent>
          </Card>
        )}
    </LeagueSettingsLayout>
  );
}
